
import React from "react";
import { useClassifications } from "@/hooks/useClassifications";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/Spinner";
import { ClassificationTree } from "./ClassificationTree";
import { ClassificationList } from "./ClassificationList";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import { AssetClassification } from "@/types/classifications";

export function ClassificationManagement() {
  const { 
    allClassifications, 
    isLoading, 
    error, 
    updateNode,
    addChildNode,
    removeNode,
    cloneClassification,
    deleteClassification,
    updateClassificationDetails,
    createClassification
  } = useClassifications();
  const [selectedClassification, setSelectedClassification] = React.useState<AssetClassification | null>(null);

  React.useEffect(() => {
    if (allClassifications?.length && !selectedClassification) {
      setSelectedClassification(allClassifications[0]);
    }
  }, [allClassifications]);

  React.useEffect(() => {
    logger.info('classifications.component', 'Classifications loaded', {
      totalCount: allClassifications?.length || 0,
      selectedId: selectedClassification?.id,
      timestamp: new Date().toISOString()
    });
  }, [allClassifications, selectedClassification]);

  const handleUpdate = async (updatedNode: any) => {
    if (!selectedClassification) return;
    try {
      await updateNode({ 
        classificationId: selectedClassification.id, 
        node: updatedNode 
      });
    } catch (error) {
      logger.error('classifications.update', 'Failed to update classification', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error("Failed to update classification");
    }
  };

  const handleAdd = async () => {
    try {
      const newClassification = {
        display_name: "New Classification",
        reference_name: "new_classification",
        status: "draft" as const,
        classification: {
          id: crypto.randomUUID(),
          name: "Root",
          children: []
        }
      };
      await createClassification(newClassification);
    } catch (error) {
      logger.error('classifications.create', 'Failed to create classification', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error("Failed to create classification");
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load classifications. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" ariaLabel="Loading classifications" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {allClassifications && (
        <ClassificationList
          classifications={allClassifications}
          onSelect={setSelectedClassification}
          onClone={cloneClassification}
          onDelete={deleteClassification}
          onEdit={(classification) => {
            if (classification.id) {
              updateClassificationDetails({
                classificationId: classification.id,
                updates: {
                  display_name: classification.display_name,
                  reference_name: classification.reference_name,
                  status: classification.status,
                  provider_type: classification.provider_type,
                  notes: classification.notes
                }
              });
            }
          }}
          onAdd={handleAdd}
          selectedId={selectedClassification?.id}
        />
      )}

      {selectedClassification && (
        <div className="border rounded-lg">
          <ScrollArea className="h-[500px] w-full">
            <div className="p-4">
              <ClassificationTree 
                classifications={selectedClassification.classification}
                onUpdate={handleUpdate}
                onAddChild={(parentId, newNode) => {
                  addChildNode({
                    classificationId: selectedClassification.id,
                    parentId,
                    newNode
                  });
                }}
                onRemove={(nodeId) => {
                  removeNode({
                    classificationId: selectedClassification.id,
                    nodeId
                  });
                }}
              />
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
